/**
 * ClientBlankSupplyGrant (Phase 92, D-08, D-22, D-24): the 3PL owner's
 * per-client "Let this client use my blanks" switch, inside each ACTIVE client
 * card of `SharedLocationsCard`, directly under the stock-writes grant, with a
 * read-only Verify under it.
 *
 * Owner side only. The card mounts in Settings, Locations (an admin-only tab),
 * and the server re-checks admin, owner and the shared-locations flag on every
 * call, so the client never sees or reaches this control.
 *
 * Turning it ON is optimistic with no confirm. Turning it OFF fails closed for
 * the client (their products on these blanks show as out of stock until it is
 * back on), so it asks first and names how many products that touches.
 *
 * Translated, unlike the rest of `features/locations/**`: the owner reading it
 * is the 3PL, whose operators may run the app in zh (precedent:
 * `SharedSkuMappingLine`).
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { AlertTriangle, Info, Loader2, ShieldCheck, Shirt } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { NoteLine } from '@/components/shared/NoteLine';
import { VerifyFacet } from '@/features/production-settings/components/VerifyFacet';
import { trpc } from '@/lib/trpc';
import { toast } from '@/hooks/use-toast';
import { JunctionGrantRow } from './JunctionGrantRow';

const CODE_TABLES_PATH = '/product-sync?tab=code';
const LINK_CLASS =
  'font-medium text-threadheads-purple underline underline-offset-2 hover:text-threadheads-purple/80 motion-reduce:transition-none';

export interface ClientBlankSupplyGrantProps {
  locationId: string;
  locationName: string;
  clientOrganizationId: string;
  /** The client's display name (the owner's nickname when set). */
  clientName: string;
  /** `client_blank_supply_enabled` on this client's connected row. */
  enabled: boolean;
}

interface JunctionKey {
  location_id?: string;
  client_organization_id?: string;
  client_blank_supply_enabled?: boolean;
}

/** `locations.verifyClientBlankSupply` (92-08 contract). */
interface ClientBlankSupplyVerifyReport {
  connected: boolean;
  consentOn: boolean;
  sharedBlankCount: number;
  unsplitBlanks: { count: number; examples: Array<{ skuId: string; sku: string }> };
  printSizeRules: { covered: number; total: number };
  defaultWeights: { covered: number; total: number };
  darkColourSuggestionsSet: boolean;
}

/** The tRPC error code (`TOO_MANY_REQUESTS`, ...). Causes are dropped on the wire, codes are not. */
function readErrorCode(error: unknown): string | undefined {
  const data = (error as { data?: { code?: unknown; httpStatus?: unknown } } | null | undefined)?.data;
  if (typeof data?.code === 'string') return data.code;
  return data?.httpStatus === 429 ? 'TOO_MANY_REQUESTS' : undefined;
}

export function ClientBlankSupplyGrant({
  locationId,
  locationName,
  clientOrganizationId,
  clientName,
  enabled,
}: ClientBlankSupplyGrantProps) {
  const { t } = useTranslation('settings');
  const utils = trpc.useUtils();
  const [confirmOpen, setConfirmOpen] = useState(false);
  // The sticky failure toast, so a later success on this row can retire it.
  const failureToast = useRef<{ dismiss: () => void } | null>(null);
  // Set synchronously on click: `isPending` only flips on the next render, so
  // two clicks in one tick would otherwise both send.
  const inFlight = useRef(false);
  // The confirm opens from the switch, not from a Radix Trigger, so Radix has
  // nothing to hand focus back to on close; send it back to the switch.
  const switchRef = useRef<HTMLButtonElement>(null);

  const isThisRow = (row: unknown): boolean => {
    const j = row as JunctionKey;
    return j.location_id === locationId && j.client_organization_id === clientOrganizationId;
  };

  // Patch only THIS row in the owner's connected-clients cache.
  const patchRow = (next: boolean) =>
    utils.locations.listConnectedClients.setData(undefined, (prev) =>
      prev?.map((row) =>
        isThisRow(row)
          ? ({ ...(row as JunctionKey), client_blank_supply_enabled: next } as typeof row)
          : row,
      ),
    );

  // How many of this client's products print on our blanks. Read only while
  // the switch is on (the in-use line) or the turn-off confirm is open; never
  // polled. No retry: a failure must show at once, not after a silent wait.
  const countQuery = trpc.locations.countClientSupplyProducts.useQuery(
    { clientOrganizationId },
    { enabled: enabled || confirmOpen, staleTime: 30_000, retry: false },
  );
  const count = countQuery.data?.count;
  const counting = countQuery.isFetching || (count === undefined && !countQuery.isError);

  const update = trpc.locations.updateClientBlankSupply.useMutation({
    // Optimistic, the `updateClientStockWrites` pattern: a bare Switch with a
    // round trip otherwise sits still and reads as "nothing happened".
    onMutate: async (vars) => {
      // A background refetch landing mid-flight would overwrite the patch.
      await utils.locations.listConnectedClients.cancel();
      // Snapshot THIS row only: a whole-list snapshot would undo a concurrent
      // toggle on another client when this one fails.
      const row = (utils.locations.listConnectedClients.getData() ?? []).find(isThisRow) as
        | JunctionKey
        | undefined;
      const previousEnabled = row?.client_blank_supply_enabled === true;
      patchRow(vars.enabled);
      return { previousEnabled };
    },
    onError: (err, _vars, ctx) => {
      patchRow(ctx?.previousEnabled ?? false);
      failureToast.current?.dismiss();
      // Errors stay until dismissed (DESIGN.md 15.5). The server's message is
      // already plain operator copy (not connected, too many requests).
      failureToast.current = toast({
        title: t('sharedLocations.blanks.toastFailed'),
        description: err.message,
        variant: 'destructive',
        duration: Infinity,
      });
    },
    onSuccess: (data, vars) => {
      failureToast.current?.dismiss();
      failureToast.current = null;
      if (typeof data.newEnabled === 'boolean') patchRow(data.newEnabled);
      // `changed: false` is a retried request whose first attempt already
      // landed: the switch is right, so say nothing a second time.
      if (data.changed) {
        toast({
          title: vars.enabled
            ? t('sharedLocations.blanks.toastOn', { client: clientName })
            : t('sharedLocations.blanks.toastOff', { client: clientName }),
        });
      }
      void utils.locations.listConnectedClients.invalidate();
      void utils.locations.countClientSupplyProducts.invalidate({ clientOrganizationId });
    },
  });

  const send = (next: boolean) => {
    if (inFlight.current || update.isPending) return;
    inFlight.current = true;
    update.mutate(
      { locationId, clientOrganizationId, enabled: next },
      {
        onSettled: () => {
          inFlight.current = false;
        },
      },
    );
  };

  const openConfirm = () => {
    setConfirmOpen(true);
    // The confirm names a number the owner acts on: re-read it unless the one
    // in hand is fresh (30 s) and good.
    if (countQuery.isStale || countQuery.isError) void countQuery.refetch();
  };

  const verify = trpc.locations.verifyClientBlankSupply.useMutation();
  // A result read before the switch moved describes the old state; drop it.
  const resetVerify = verify.reset;
  useEffect(() => {
    resetVerify();
  }, [enabled, resetVerify]);
  const verifyFailure = verify.error && !verify.isPending ? verify.error : null;

  const switchId = `client-blanks-${clientOrganizationId}`;
  const helpId = `client-blanks-help-${clientOrganizationId}`;

  return (
    <>
      <JunctionGrantRow
        id={switchId}
        helpId={helpId}
        label={t('sharedLocations.blanks.label')}
        help={<span className="[overflow-wrap:anywhere]">{t('sharedLocations.blanks.help', { client: clientName })}</span>}
        control={
          <Switch
            ref={switchRef}
            id={switchId}
            aria-describedby={helpId}
            checked={enabled}
            // No `disabled` while pending: a disabled focused Radix Switch
            // blurs to <body>. The optimistic patch already moved the thumb;
            // swallow re-clicks until this row's write settles.
            onCheckedChange={(checked) => {
              if (inFlight.current || update.isPending) return;
              if (checked) send(true);
              else openConfirm();
            }}
          />
        }
      >
        {/* Quiet by default: nothing at zero or while loading, so the row
            never shifts; a failed read says so instead of disappearing. */}
        {enabled &&
          (count !== undefined ? (
            count > 0 ? (
              <NoteLine icon={Shirt} className="mt-2 [overflow-wrap:anywhere]">
                {t('sharedLocations.blanks.inUse', { count, client: clientName })}
              </NoteLine>
            ) : null
          ) : countQuery.isError ? (
            <NoteLine icon={AlertTriangle} tone="warning" className="mt-2">
              {t('sharedLocations.blanks.countFailed')}
            </NoteLine>
          ) : null)}
      </JunctionGrantRow>

      <div className="mt-4 space-y-2">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <Button
            type="button"
            variant="outline"
            className="h-8 gap-1.5 px-3 text-xs motion-reduce:transition-none"
            onClick={() => verify.mutate({ locationId, clientOrganizationId })}
            disabled={verify.isPending}
          >
            {verify.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
            ) : (
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {t('sharedLocations.blanks.verify.button')}
          </Button>
          <p className="max-w-prose text-xs text-muted-foreground [overflow-wrap:anywhere]">
            {t('sharedLocations.blanks.verify.intro', { client: clientName })}
          </p>
        </div>
        {/* Always mounted; only its content swaps, so screen readers announce
            the outcome (DESIGN.md section 16). Empty before the first run. */}
        <div role="status" aria-live="polite">
          {verify.isPending ? (
            <span className="sr-only">{t('sharedLocations.blanks.verify.running')}</span>
          ) : verifyFailure ? (
            <p className="text-xs text-destructive">
              {readErrorCode(verifyFailure) === 'TOO_MANY_REQUESTS'
                ? t('storeSkus.verify.tooMany')
                : t('sharedLocations.blanks.verify.error')}
            </p>
          ) : verify.data ? (
            <VerifyResults report={verify.data} clientName={clientName} locationName={locationName} />
          ) : null}
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            switchRef.current?.focus();
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="[overflow-wrap:anywhere]">
              {t('sharedLocations.blanks.confirm.title', { client: clientName })}
            </AlertDialogTitle>
            <AlertDialogDescription className="[overflow-wrap:anywhere]">
              {counting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                  {t('sharedLocations.blanks.counting')}
                </span>
              ) : countQuery.isError || count === undefined ? (
                // The count could not be read: say what turning off does
                // anyway, and never block the owner on it.
                t('sharedLocations.blanks.confirm.descUnknown')
              ) : count === 0 ? (
                t('sharedLocations.blanks.confirm.descZero')
              ) : (
                t('sharedLocations.blanks.confirm.descCount', { count, client: clientName })
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('sharedLocations.blanks.confirm.keep')}</AlertDialogCancel>
            {/* Reversible: orange outline, never red (DESIGN.md 13.6). */}
            <AlertDialogAction
              className="border border-orange-300 bg-background text-orange-700 hover:bg-orange-50 hover:text-orange-700"
              disabled={counting}
              onClick={() => send(false)}
            >
              {t('sharedLocations.blanks.confirm.turnOff')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

interface VerifyResultsProps {
  report: ClientBlankSupplyVerifyReport;
  clientName: string;
  locationName: string;
}

/** Six facets, each with its own pass or fail; a failing one never disables the switch. */
function VerifyResults({ report, clientName, locationName }: VerifyResultsProps) {
  const { t } = useTranslation('settings');
  const sr = { srPassedLabel: t('sharedLocations.blanks.verify.passed'), srFailedLabel: t('sharedLocations.blanks.verify.needsAttention') };
  const names = { client: clientName, location: locationName };
  const unsplit = report.unsplitBlanks;
  const noBlanks = report.sharedBlankCount === 0;

  let splitLabel: string;
  if (noBlanks) splitLabel = t('sharedLocations.blanks.verify.splitNone');
  else if (unsplit.count === 0) splitLabel = t('sharedLocations.blanks.verify.splitOk', { count: report.sharedBlankCount });
  else splitLabel = t('sharedLocations.blanks.verify.splitFail', { count: unsplit.count });

  return (
    <div className="space-y-2 rounded-md border bg-background p-4 [overflow-wrap:anywhere]">
      <VerifyFacet
        ok={report.connected}
        label={report.connected ? t('sharedLocations.blanks.verify.connectedOk', names) : t('sharedLocations.blanks.verify.connectedFail', names)}
        {...sr}
      />
      <VerifyFacet
        ok={report.consentOn}
        label={report.consentOn ? t('sharedLocations.blanks.verify.consentOk', names) : t('sharedLocations.blanks.verify.consentFail', names)}
        {...sr}
      />
      <VerifyFacet
        ok={!noBlanks}
        label={
          noBlanks
            ? t('sharedLocations.blanks.verify.blanksFail')
            : t('sharedLocations.blanks.verify.blanksOk', { count: report.sharedBlankCount, location: locationName })
        }
        {...sr}
      />
      <VerifyFacet ok={unsplit.count === 0} label={splitLabel} {...sr}>
        {unsplit.count > 0 ? (
          <div className="space-y-1">
            {unsplit.examples.length > 0 && (
              <ul className="space-y-0.5">
                {unsplit.examples.map((blank) => (
                  <li key={blank.skuId} className="min-w-0 break-all font-mono">
                    {blank.sku}
                  </li>
                ))}
              </ul>
            )}
            <Link to={CODE_TABLES_PATH} className={LINK_CLASS}>
              {t('sharedLocations.blanks.verify.openCodeTables')}
            </Link>
          </div>
        ) : null}
      </VerifyFacet>
      <VerifyFacet
        ok={report.printSizeRules.covered === report.printSizeRules.total}
        label={t('sharedLocations.blanks.verify.sizeRules', report.printSizeRules)}
        {...sr}
      />
      <VerifyFacet
        ok={report.defaultWeights.covered === report.defaultWeights.total}
        label={t('sharedLocations.blanks.verify.weights', report.defaultWeights)}
        {...sr}
      />
      {/* Information, never a failure (D-24): the client picks light or dark. */}
      {!report.darkColourSuggestionsSet && (
        <NoteLine icon={Info}>{t('sharedLocations.blanks.verify.noColourSuggestions')}</NoteLine>
      )}
    </div>
  );
}

export interface ClientBlankSupplyMirrorLineProps {
  /** `client_blank_supply_enabled` on the client's own shared-location row. */
  enabled: boolean;
  /** The 3PL's organisation name. */
  ownerName: string;
}

/**
 * The CLIENT's read-only mirror of the consent above (UI-SPEC 2.4), shown in
 * `ConnectedVia3PLSection` beside the stock-writes mirror. Nothing when off.
 */
export function ClientBlankSupplyMirrorLine({ enabled, ownerName }: ClientBlankSupplyMirrorLineProps) {
  const { t } = useTranslation('settings');
  if (!enabled) return null;
  return (
    <div className="mt-2 flex items-start gap-2 text-xs text-muted-foreground">
      <Shirt className="mt-px h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden="true" />
      <span className="[overflow-wrap:anywhere]">
        <Trans
          t={t}
          i18nKey="sharedLocations.blanks.mirror.line"
          values={{ owner: ownerName, link: t('sharedLocations.blanks.mirror.link') }}
          // `<page>`, not `<link>`: i18next parses `link` as a void HTML
          // element and would drop the words inside it.
          components={{
            page: <Link to="/product-sync" className="underline underline-offset-2 hover:text-foreground" />,
          }}
        />
      </span>
    </div>
  );
}
